const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * The access token lives in memory only — never localStorage, which is readable
 * by any injected script. Durability comes from the httpOnly refresh cookie.
 */
let accessToken: string | null = null;
const subscribers = new Set<(token: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const notify of subscribers) notify(token);
}

export function onAccessTokenChange(listener: (token: string | null) => void): () => void {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
}

let refreshInFlight: Promise<string | null> | null = null;

/** Concurrent 401s share one refresh round trip instead of racing. */
export function refreshSession(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        setAccessToken(null);
        return null;
      }
      const data = (await response.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      return data.accessToken;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /**
   * A multipart body, for the one thing the API takes as a file: a support
   * ticket attachment. Mutually exclusive with `body`, and the content type is
   * deliberately left unset — the browser has to write the boundary into it,
   * and a hand-set `multipart/form-data` header produces a request no server
   * can parse.
   */
  form?: FormData;
  signal?: AbortSignal;
  /** Skip the refresh-and-retry dance (used by the auth endpoints themselves). */
  anonymous?: boolean;
  /**
   * Send an `Idempotency-Key`, so a request the API already carried out is
   * answered again rather than carried out twice.
   *
   * `true` derives the key from the request itself -- method, path and body,
   * salted per tab -- which is the right shape for a create form: the same
   * form submitted again after a lost response replays the app that already
   * exists, and a form with a changed name is a new key and a new app. A
   * string is used as given, for the one case where an identical body is a
   * different intent: two $25 top-ups in a row are two top-ups, so that
   * caller mints a fresh key per attempt and gets only the retry safety.
   *
   * Either way the key survives the 401 refresh-and-resend below, which is
   * the retry this codebase actually performs.
   */
  idempotent?: boolean | string;
  /**
   * Send no cookie, for the handful of reads the API serves to everybody.
   *
   * Those routes answer with `Access-Control-Allow-Origin: *` -- they are read
   * by the marketing site too, and their responses are cached at the edge under
   * one key, which a per-origin header would make unsafe. A browser refuses a
   * wildcard on a request that carries credentials, so sending the cookie to one
   * of them does not merely waste it: the fetch fails before the handler is ever
   * reached, and the caller sees a network error it cannot tell from an outage.
   *
   * Separate from `anonymous` on purpose. That one skips the token; this one
   * skips the cookie, and the auth routes need exactly the opposite pair -- no
   * bearer, but the refresh cookie is the entire point of the call.
   */
  omitCredentials?: boolean;
}

/**
 * One value per tab, so a derived key never collides with a previous visit's:
 * a reload that submits the same form again is a person who wants another one.
 */
const TAB_NONCE = crypto.randomUUID();

async function derivedKey(path: string, options: RequestOptions): Promise<string> {
  const material = `${TAB_NONCE}\n${options.method ?? "GET"}\n${path}\n${JSON.stringify(options.body ?? null)}`;
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(material));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers = new Headers();
  if (options.body !== undefined) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (options.idempotent) {
    headers.set(
      "idempotency-key",
      typeof options.idempotent === "string" ? options.idempotent : await derivedKey(path, options),
    );
  }

  const body = options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined);

  return fetch(`${API_URL}${path}`, {
    method: options.method ?? "GET",
    headers,
    credentials: options.omitCredentials ? "omit" : "include",
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options, options.anonymous ? null : accessToken);

  if (response.status === 401 && !options.anonymous) {
    const renewed = await refreshSession();
    if (renewed) response = await send(path, options, renewed);
  }

  /*
   * The API is still running the first copy of this request -- a double
   * submit that beat the disabled button, or the refresh resend above
   * arriving while the original is in flight. It asks for a second, so wait
   * one and ask once more; the answer then is the replay of whatever the
   * first copy produced.
   */
  if (response.status === 409 && options.idempotent && (await isInProgress(response.clone()))) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    response = await send(path, options, options.anonymous ? null : accessToken);
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

async function isInProgress(response: Response): Promise<boolean> {
  try {
    const body = (await response.json()) as { error?: { code?: string } };
    return body.error?.code === "idempotency_in_progress";
  } catch {
    return false;
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = "unknown_error";
  let message = `Request failed with status ${response.status}`;
  let details: unknown;

  try {
    const body = (await response.json()) as {
      success?: boolean;
      error?: { code?: string; message?: string; details?: unknown; issues?: Issue[] };
    };
    if (body.error && body.success === false && Array.isArray(body.error.issues)) {
      // A schema refusal from the API's validator, which answers 400 with the
      // raw zod error rather than the platform's own shape. Its `message` is
      // the issue list serialised as JSON, which is not a sentence; the first
      // issue, with the field it names, is.
      code = "validation_failed";
      message = firstIssue(body.error.issues) ?? message;
      details = body.error.issues;
    } else if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // Non-JSON error body; keep the status-derived message.
  }

  return new ApiError(response.status, code, message, details);
}

interface Issue {
  path?: (string | number)[];
  message?: string;
}

/** `source.branch: Use letters, numbers…` — the field, then what was wrong with it. */
function firstIssue(issues: Issue[]): string | null {
  const issue = issues.find((candidate) => typeof candidate.message === "string");
  if (!issue?.message) return null;
  const field = (issue.path ?? []).filter((part) => typeof part === "string").join(".");
  return field ? `${field}: ${issue.message}` : issue.message;
}

/**
 * Uploads one file and returns whatever the endpoint answers with.
 *
 * Its own function rather than a flag on `api`, because the retry it shares
 * with everything else has a catch here: a `File` can be read only once by
 * `fetch`, but a fresh `FormData` is built per attempt from the same file
 * handle, so the 401-refresh-retry path works exactly as it does for JSON.
 */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const build = () => {
    const form = new FormData();
    form.append("file", file);
    return form;
  };

  let response = await send(path, { method: "POST", form: build() }, accessToken);

  if (response.status === 401) {
    const renewed = await refreshSession();
    if (renewed) response = await send(path, { method: "POST", form: build() }, renewed);
  }

  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}

/**
 * Fetches a file the session is entitled to, as a blob.
 *
 * Attachments cannot be a plain `<a href>`: the API authorizes with a bearer
 * token held in memory, and an anchor sends no header. So the bytes come back
 * through here and the caller turns them into an object URL — which is also
 * what makes an inline image preview possible without ever putting a
 * customer's file on this origin as a document.
 */
export async function apiBlob(path: string): Promise<Blob> {
  let response = await send(path, {}, accessToken);

  if (response.status === 401) {
    const renewed = await refreshSession();
    if (renewed) response = await send(path, {}, renewed);
  }

  if (!response.ok) throw await toApiError(response);
  return response.blob();
}

export { API_URL };
